import type { AcousticCalibrationSnapshot, AcousticCalibrationTag } from './calibrationTypes';
import { ACOUSTIC_CALIBRATION_TAGS } from './calibrationTypes';

export const CALIBRABLE_SITUATION_TAGS: AcousticCalibrationTag[] = [
  'babble',
  'shouting',
  'single_speaker',
  'conversation'
];

export const MIN_SNAPSHOTS_PER_CATEGORY = 2;

export type CalibrationReadiness = {
  calibrableSnapshotCount: number;
  silenceSnapshotCount: number;
  readyTags: AcousticCalibrationTag[];
  missingTags: Array<{ tag: AcousticCalibrationTag; label: string; count: number }>;
  canActivateGlobally: boolean;
  canApplySilenceBaseline: boolean;
  blockReason: string | null;
};

export function getCalibrationReadiness(snapshots: AcousticCalibrationSnapshot[]): CalibrationReadiness {
  const silenceSnapshotCount = snapshots.filter((s) => s.tag === 'silence').length;
  const calibrableSnapshotCount = snapshots.filter((s) =>
    CALIBRABLE_SITUATION_TAGS.includes(s.tag)
  ).length;

  const readyTags: AcousticCalibrationTag[] = [];
  const missingTags: CalibrationReadiness['missingTags'] = [];

  for (const tag of CALIBRABLE_SITUATION_TAGS) {
    const count = snapshots.filter((s) => s.tag === tag).length;
    const label = ACOUSTIC_CALIBRATION_TAGS.find((t) => t.key === tag)?.label ?? tag;
    if (count >= MIN_SNAPSHOTS_PER_CATEGORY) {
      readyTags.push(tag);
    } else {
      missingTags.push({ tag, label, count });
    }
  }

  const canActivateGlobally = readyTags.length > 0;
  const canApplySilenceBaseline = silenceSnapshotCount >= MIN_SNAPSHOTS_PER_CATEGORY;
  let blockReason: string | null = null;

  if (!canActivateGlobally) {
    if (snapshots.length === 0) {
      blockReason = 'Noch keine Snapshots – bitte Situationen taggen (z. B. Gemurmel, Geschrei).';
    } else if (calibrableSnapshotCount === 0 && silenceSnapshotCount > 0) {
      blockReason = `${silenceSnapshotCount} Snapshot${silenceSnapshotCount === 1 ? '' : 's'} mit Tag „Stille“. Stille erfasst den Rauschboden (Lüftung, Grundrauschen) – damit „Alle Kategorien global aktivieren“ nutzt du zusätzlich je ${MIN_SNAPSHOTS_PER_CATEGORY}× Gemurmel, Geschrei, Einzelsprecher oder Dialog. Alternativ: „Rauschboden aus Stille“ (ab ${MIN_SNAPSHOTS_PER_CATEGORY} Stille-Snapshots).`;
    } else if (calibrableSnapshotCount > 0) {
      const examples = missingTags
        .filter((m) => m.count > 0)
        .map((m) => `${m.label} (${m.count}/${MIN_SNAPSHOTS_PER_CATEGORY})`)
        .join(', ');
      blockReason = examples
        ? `Noch zu wenige Snapshots pro Kategorie: ${examples}. Pro Kategorie mindestens ${MIN_SNAPSHOTS_PER_CATEGORY} nötig.`
        : `Pro Situations-Kategorie mindestens ${MIN_SNAPSHOTS_PER_CATEGORY} Snapshots taggen (z. B. Gemurmel, Geschrei).`;
    } else {
      blockReason = `Pro Situations-Kategorie mindestens ${MIN_SNAPSHOTS_PER_CATEGORY} Snapshots taggen.`;
    }
  }

  return {
    calibrableSnapshotCount,
    silenceSnapshotCount,
    readyTags,
    missingTags,
    canActivateGlobally,
    canApplySilenceBaseline,
    blockReason
  };
}
